/**
 * Module-scoped cache for abilities data.
 *
 * Without this, every navigation re-mounts the page and re-runs all fetches —
 * for essentials that's 1 ability query + 2 index queries + 15 parallel
 * SKILL.md enrichment fetches + 6 parallel agent.md fetches = 24 round-trips
 * just to render the inside-ability page. With this cache, return-navigation
 * shows the previous data instantly and revalidates in background.
 *
 * Module scope persists across navigations within the same browser tab. Cache
 * dies on hard refresh (which is fine — staging deploys publish fresh code).
 *
 * Phase 2 + Phase 3 cache surfaces were merged here when /marketplace was
 * unified into /abilities. The list/detail catalog caches keyed off (q +
 * category) and slug respectively now live alongside the per-ability + per-
 * skill + per-agent drill-in caches.
 */
import type {
  Ability,
  AbilityFile,
  CatalogAbility,
  CatalogAbilityDetail,
  CatalogSkill,
  CatalogAgent,
} from "@bash/shared";

// ---------------------------------------------------------------------------
// Ability-level cache: parent ability + skill/agent index rows + enriched md
// ---------------------------------------------------------------------------

export interface SkillIndexRow {
  ability_id: string;
  skill_slug: string;
  name: string | null;
  description: string | null;
}

export interface AgentIndexRow {
  ability_id: string;
  agent_slug: string;
  name: string | null;
  description: string | null;
}

export interface EnrichedRow {
  slug: string;
  name: string;
  description: string;
  icon?: string;
}

export interface AbilityIndexCache {
  ability: Ability;
  skillRows: SkillIndexRow[];
  agentRows: AgentIndexRow[];
  enrichedSkills: EnrichedRow[] | null;
  enrichedAgents: EnrichedRow[] | null;
  claudeMd: string | null | undefined;
  manifestContent: string | null | undefined;
}

const abilityCacheBySlug = new Map<string, AbilityIndexCache>();

export function getAbilityCache(slug: string): AbilityIndexCache | undefined {
  return abilityCacheBySlug.get(slug);
}

export function setAbilityCache(
  slug: string,
  patch: Partial<AbilityIndexCache>
) {
  const existing = abilityCacheBySlug.get(slug);
  if (existing) {
    abilityCacheBySlug.set(slug, { ...existing, ...patch });
  } else if (patch.ability) {
    abilityCacheBySlug.set(slug, {
      ability: patch.ability,
      skillRows: patch.skillRows ?? [],
      agentRows: patch.agentRows ?? [],
      enrichedSkills: patch.enrichedSkills ?? null,
      enrichedAgents: patch.enrichedAgents ?? null,
      claudeMd: patch.claudeMd,
      manifestContent: patch.manifestContent,
    });
  }
}

export function invalidateAbilityCache(slug?: string) {
  if (slug) abilityCacheBySlug.delete(slug);
  else abilityCacheBySlug.clear();
}

// ---------------------------------------------------------------------------
// File-list cache (key: `${abilityId}:${prefix}`) and content cache
// ---------------------------------------------------------------------------

const filesCache = new Map<string, AbilityFile[]>();
const contentCache = new Map<string, string>();

function fileKey(abilityId: string, prefix: string) {
  return `${abilityId}:${prefix}`;
}

export function getFilesCache(
  abilityId: string,
  prefix: string
): AbilityFile[] | undefined {
  return filesCache.get(fileKey(abilityId, prefix));
}

export function setFilesCache(
  abilityId: string,
  prefix: string,
  files: AbilityFile[]
) {
  filesCache.set(fileKey(abilityId, prefix), files);
}

export function getContentCache(
  abilityId: string,
  path: string
): string | undefined {
  return contentCache.get(fileKey(abilityId, path));
}

export function setContentCache(
  abilityId: string,
  path: string,
  content: string
) {
  contentCache.set(fileKey(abilityId, path), content);
}

// ---------------------------------------------------------------------------
// Skill drill-in cache (key: `${abilityId}:${skillSlug}`) — frontmatter + body
// ---------------------------------------------------------------------------

export interface SkillDetailCache {
  frontmatter: Record<string, string>;
  // The skill's files list (under the `skills/<slug>/` prefix) is in filesCache.
}

const skillDetailCache = new Map<string, SkillDetailCache>();

export function getSkillDetailCache(
  abilityId: string,
  skillSlug: string
): SkillDetailCache | undefined {
  return skillDetailCache.get(`${abilityId}:${skillSlug}`);
}

export function setSkillDetailCache(
  abilityId: string,
  skillSlug: string,
  data: SkillDetailCache
) {
  skillDetailCache.set(`${abilityId}:${skillSlug}`, data);
}

// ---------------------------------------------------------------------------
// Agent drill-in cache (key: `${abilityId}:${agentSlug}`)
// ---------------------------------------------------------------------------

export interface AgentDetailCache {
  name: string;
  description: string;
  tools: string[];
  skills: string[];
  body: string | null;
}

const agentDetailCache = new Map<string, AgentDetailCache>();

export function getAgentDetailCache(
  abilityId: string,
  agentSlug: string
): AgentDetailCache | undefined {
  return agentDetailCache.get(`${abilityId}:${agentSlug}`);
}

export function setAgentDetailCache(
  abilityId: string,
  agentSlug: string,
  data: AgentDetailCache
) {
  agentDetailCache.set(`${abilityId}:${agentSlug}`, data);
}

// ---------------------------------------------------------------------------
// Catalog list + detail caches (unified /abilities surface — Phase 2 + 3)
//
// listCache  : keyed by `${q}|${category}` filter pair, value = CatalogAbility[]
// detailCache: keyed by slug, value = CatalogAbilityDetail
//
// Both are module-scoped Maps. The list cache key matches what /abilities
// composes (lowercased trimmed q + 'All'-normalised category), so revisiting
// the same filter returns instantly. The detail cache lets card hover →
// drill-in click feel instant by pre-populating on prefetchAbility().
// ---------------------------------------------------------------------------

const catalogListCache = new Map<string, CatalogAbility[]>();
const catalogDetailCache = new Map<string, CatalogAbilityDetail>();

export function getCachedList(key: string): CatalogAbility[] | undefined {
  return catalogListCache.get(key);
}

export function setCachedList(key: string, value: CatalogAbility[]): void {
  catalogListCache.set(key, value);
}

export function getCachedDetail(
  slug: string
): CatalogAbilityDetail | undefined {
  return catalogDetailCache.get(slug);
}

export function setCachedDetail(
  slug: string,
  value: CatalogAbilityDetail
): void {
  catalogDetailCache.set(slug, value);
}

export function invalidateDetail(slug: string): void {
  catalogDetailCache.delete(slug);
}

/**
 * Blow away every abilities cache surface. Called after install / uninstall /
 * publish so the next render sees fresh state. Replaces the old
 * invalidateAllMarketplace + invalidateAbilityCache pair from the split-
 * surface era.
 */
export function invalidateAllAbilities(): void {
  catalogListCache.clear();
  catalogDetailCache.clear();
  abilityCacheBySlug.clear();
  filesCache.clear();
  contentCache.clear();
  skillDetailCache.clear();
  agentDetailCache.clear();
  skillsCatalogCache.clear();
  agentsCatalogCache.clear();
}

// ---------------------------------------------------------------------------
// Skills + Agents catalog caches (V1d — three-tab /abilities surface)
//
// Mirror the abilities catalogListCache pattern. Keyed by `${q}|${category}`
// for the filter pair. Cleared by invalidateAllAbilities() above so a fresh
// state lands across all three tabs after any install/uninstall.
// ---------------------------------------------------------------------------

const skillsCatalogCache = new Map<string, CatalogSkill[]>();
const agentsCatalogCache = new Map<string, CatalogAgent[]>();

export function getCachedSkills(key: string): CatalogSkill[] | undefined {
  return skillsCatalogCache.get(key);
}

export function setCachedSkills(key: string, value: CatalogSkill[]): void {
  skillsCatalogCache.set(key, value);
}

export function getCachedAgents(key: string): CatalogAgent[] | undefined {
  return agentsCatalogCache.get(key);
}

export function setCachedAgents(key: string, value: CatalogAgent[]): void {
  agentsCatalogCache.set(key, value);
}
